import { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart3,
  BookOpenCheck,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Link2,
  PieChart as PieChartIcon,
  Search,
  TrendingDown,
  TrendingUp,
  Users,
  UserCheck,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  Badge,
  Card,
  CHART_COLORS,
  PageHeader,
  Progress,
  StatCard,
  Tabs,
} from "../adminComponents/adminComponents";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useAdminTrainingHook } from "../../hooks/useAdminHooks";
import { useManagerTrainingHook } from "../../hooks/useManagerHooks";
// Pagination footer reuses the .adminVentures-pagination classes so the
// two tables stay visually identical. Import the stylesheet here so the
// classes are available regardless of navigation order.
import "../ventures/ventures.css";
import "./training.css";

// Tab definitions matching the reference Training page.
const TABS = [
  { value: "dashboard", label: "Dashboard" },
  { value: "trainers", label: "Trainers" },
  { value: "participants", label: "Participants & Progress" },
];

const SUMMARY_CARD_ICONS = {
  users: Users,
  "user-check": UserCheck,
  "pie-chart": PieChartIcon,
  "check-circle": CheckCircle2,
  "book-open-check": BookOpenCheck,
  "trending-down": TrendingDown,
  "graduation-cap": GraduationCap,
  "link-2": Link2,
};

// Empty-state fallback so the page renders cleanly before data arrives.
const DEFAULT_TRAINING = {
  summary_cards: [],
  attendance_trend: [],
  participant_status: [],
  cohort_performance: [],
  program_line_performance: [],
  trainer_summary: {
    community_units: 0,
    total_enrollment: 0,
    active: 0,
    average_attendance: 0,
  },
  trainer_performance: [],
  session_attendance_matrix: [],
  cohorts: [],
  participants: [],
};

const percent = (value) => `${Math.round(Number(value) || 0)}%`;

function buildSearchableText(value, seen = new Set()) {
  if (value == null) {
    return "";
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value).toLowerCase();
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => buildSearchableText(item, seen))
      .filter(Boolean)
      .join(" ");
  }

  if (typeof value === "object") {
    if (seen.has(value)) {
      return "";
    }

    seen.add(value);
    return Object.values(value)
      .map((item) => buildSearchableText(item, seen))
      .filter(Boolean)
      .join(" ");
  }

  return String(value).toLowerCase();
}

// Maps domain statuses to existing Badge tones to keep colour usage consistent.
function badgeToneForStatus(status) {
  switch (status) {
    case "Active":
    case "Ready":
    case "Linked":
    case "Completed":
      return "success";
    case "At Risk":
    case "Near Ready":
    case "Needs Support":
    case "Pending Review":
    case "Developing":
      return "warning";
    case "Dropped Out":
    case "Not Eligible":
    case "Critical":
      return "danger";
    case "Graduated":
      return "primary";
    default:
      return "muted";
  }
}

// Tones for individual heatmap cells based on the raw attendance count.
function heatmapToneClass(value) {
  if (value >= 30) return "adminTraining-heat-strong";
  if (value >= 22) return "adminTraining-heat-good";
  if (value >= 15) return "adminTraining-heat-warn";
  return "adminTraining-heat-low";
}

function MultiSelectDropdown({
  label,
  placeholder,
  options,
  selected,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  // Compact trigger label: placeholder when empty, single label when 1
  let triggerText = placeholder;
  if (selected.length === 1) {
    const match = options.find((option) => option.value === selected[0]);
    triggerText = match ? match.label : placeholder;
  } else if (selected.length > 1) {
    triggerText = `${selected.length} selected`;
  }

  return (
    <div className="adminTraining-multiselect" ref={containerRef}>
      <button
        type="button"
        className="adminTraining-select adminTraining-multiselectTrigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span
          className={
            selected.length === 0
              ? "adminTraining-multiselectPlaceholder"
              : undefined
          }
        >
          {triggerText}
        </span>
        <ChevronDown
          className="adminTraining-multiselectChevron"
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="adminTraining-multiselectMenu" role="listbox">
          {selected.length > 0 && (
            <button
              type="button"
              className="adminTraining-multiselectClear"
              onClick={() => onChange([])}
            >
              Clear selection
            </button>
          )}
          {options.length === 0 ? (
            <div className="adminTraining-multiselectEmpty">No options</div>
          ) : (
            options.map((option) => {
              const active = selected.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className="adminTraining-multiselectOption"
                  onClick={() => toggle(option.value)}
                >
                  <span
                    className={
                      "adminTraining-multiselectCheckbox" +
                      (active
                        ? " adminTraining-multiselectCheckbox-active"
                        : "")
                    }
                  >
                    {active && <Check aria-hidden="true" />}
                  </span>
                  <span>{option.label}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function usePagination({ items, resetKey, isMobile }) {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState("10");
  const total = items.length;

  // Snap back to page 1 when the caller-declared inputs change.
  useEffect(() => {
    setPage(1);
  }, [resetKey, rowsPerPage]);

  // Shrink to exact row count when the list is tiny (<10); restore to 10
  useEffect(() => {
    if (total > 0 && total < 10) {
      setRowsPerPage(String(total));
    } else if (total >= 10 && Number(rowsPerPage) < 10) {
      setRowsPerPage("10");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const pageSize = Number(rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginated = items.slice(startIndex, startIndex + pageSize);

  const pageItems = useMemo(() => {
    if (isMobile) {
      if (totalPages <= 4) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
      }
      const compact = [1, safePage, totalPages]
        .filter((p, index, pages) => pages.indexOf(p) === index)
        .sort((a, b) => a - b);
      const items = [];
      for (let index = 0; index < compact.length; index += 1) {
        const p = compact[index];
        const prev = compact[index - 1];
        if (index > 0 && p - prev > 1) items.push("ellipsis");
        items.push(p);
      }
      return items;
    }

    if (totalPages <= 6) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const first = [1, 2];
    const last = [totalPages - 1, totalPages];
    const middle = safePage > 2 && safePage < totalPages - 1 ? [safePage] : [];
    const merged = [...first, ...middle, ...last]
      .filter((p, index, pages) => pages.indexOf(p) === index)
      .sort((a, b) => a - b);
    const list = [];
    for (let index = 0; index < merged.length; index += 1) {
      const p = merged[index];
      const prev = merged[index - 1];
      if (index > 0 && p - prev > 1) list.push("ellipsis");
      list.push(p);
    }
    return list;
  }, [isMobile, totalPages, safePage]);

  return {
    total,
    paginated,
    pageItems,
    safePage,
    totalPages,
    rowsPerPage,
    setRowsPerPage,
    setPage,
  };
}

// Render helper for the pagination footer — reuses the ventures classes so
// the visual output stays in sync.
function PaginationFooter({ pagination, id, label }) {
  const {
    total,
    pageItems,
    safePage,
    totalPages,
    rowsPerPage,
    setRowsPerPage,
    setPage,
  } = pagination;

  if (total === 0) return null;

  return (
    <div className="adminVentures-pagination">
      <div className="adminVentures-pageSizeWrap">
        <label className="adminVentures-pageSizeLabel" htmlFor={id}>
          Showing
        </label>
        <select
          id={id}
          className="admin-select adminVentures-pageSize"
          value={rowsPerPage}
          onChange={(event) => setRowsPerPage(event.target.value)}
          aria-label="Rows per page"
        >
          {total > 0 && total < 10 && (
            <option value={String(total)}>{total}</option>
          )}
          <option value="10">10</option>
          <option value="30">30</option>
          <option value="50">50</option>
        </select>
        <p className="admin-muted adminVentures-count">
          of {total} {label}
        </p>
      </div>
      <div className="adminVentures-paginationControls">
        <button
          type="button"
          className="adminVentures-pageButton"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={safePage <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>

        {pageItems.map((item, index) =>
          item === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              className="adminVentures-pageEllipsis"
            >
              ...
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={
                "adminVentures-pageButton" +
                (safePage === item ? " adminVentures-pageButton-active" : "")
              }
              onClick={() => setPage(item)}
            >
              {item}
            </button>
          ),
        )}

        <button
          type="button"
          className="adminVentures-pageButton"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={safePage >= totalPages}
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

export default function Training() {
  const { user, token } = useAuth();
  const isAdmin = user?.role === ROLES.ADMIN;
  const isManager = user?.role === ROLES.MANAGER;
  const [tab, setTab] = useState("dashboard");
  const [filters, setFilters] = useState({
    search: "",
    cohort: [],
  });

  // Trainers tab keeps its own cohort filter so its cards / leaderboard /
  // table / chart can be scoped to specific cohorts independently of the
  // participants view. The session-attendance heatmap is intentionally
  // left unfiltered because sessions are logged per community, not per
  // cohort (see filteredTrainerPerformance comment below).
  const [trainerFilters, setTrainerFilters] = useState({ cohort: [] });

  const adminTrainingQuery = useAdminTrainingHook({
    token,
    userId: user?.id,
    enabled: isAdmin,
  });

  const managerTrainingQuery = useManagerTrainingHook({
    token,
    userId: user?.id,
    enabled: isManager,
  });

  const data = isManager ? managerTrainingQuery.data : adminTrainingQuery.data;
  const isLoading = isManager
    ? managerTrainingQuery.isLoading
    : adminTrainingQuery.isLoading;

  const training = data ?? DEFAULT_TRAINING;
  const summaryCards = training.summary_cards ?? [];

  const communityUnitOptions = useMemo(() => {
    const unitsFromTrainerPerformance = training.trainer_performance
      .map((row) => row?.community_unit)
      .filter(Boolean);

    if (unitsFromTrainerPerformance.length > 0) {
      return [...new Set(unitsFromTrainerPerformance)];
    }

    const unitsFromParticipants = training.participants
      .map((participant) => participant?.community_unit)
      .filter(Boolean);

    if (unitsFromParticipants.length > 0) {
      return [...new Set(unitsFromParticipants)];
    }

    const firstMatrixRow = training.session_attendance_matrix?.[0];
    if (!firstMatrixRow) {
      return [];
    }

    return (firstMatrixRow.community_attendance ?? [])
      .map((entry) => entry?.community)
      .filter(Boolean);
  }, [
    training.trainer_performance,
    training.participants,
    training.session_attendance_matrix,
  ]);

  // Use data-derived units so heatmap columns stay in sync with API payloads.
  const heatmapUnits = useMemo(() => {
    if (communityUnitOptions.length > 0) {
      return communityUnitOptions;
    }

    const firstMatrixRow = training.session_attendance_matrix?.[0];
    if (!firstMatrixRow) {
      return [];
    }

    return (firstMatrixRow.community_attendance ?? [])
      .map((entry) => entry?.community)
      .filter(Boolean);
  }, [communityUnitOptions, training.session_attendance_matrix]);

  // Participant filtering — pure derivation from API data.
  const filteredParticipants = useMemo(() => {
    const searchTerm = filters.search.trim().toLowerCase();
    return training.participants.filter((participant) => {
      const matchesSearch =
        !searchTerm || buildSearchableText(participant).includes(searchTerm);
      const matchesCohort =
        filters.cohort.length === 0 ||
        filters.cohort.includes(participant.cohort_id);
      return matchesSearch && matchesCohort;
    });
  }, [training.participants, filters]);

  // Shared mobile-breakpoint state — pagination controls collapse to a
  // compact layout on narrow screens.
  const [isMobilePagination, setIsMobilePagination] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 640px)");
    const applyMatch = (event) => setIsMobilePagination(event.matches);
    setIsMobilePagination(mediaQuery.matches);
    mediaQuery.addEventListener("change", applyMatch);
    return () => mediaQuery.removeEventListener("change", applyMatch);
  }, []);

  // Cohort-filtered trainer view. When no cohorts are selected the raw
  // backend rows pass through untouched. Otherwise, we re-derive per-
  // community enrollment counts from the participants array (which
  // carries cohort_id per person). Session-based columns
  // (sessions_delivered, planned_sessions, average_attendance) stay
  // as-is because sessions are logged per community, not per cohort —
  // cohort-splitting them would misrepresent the data.
  const filteredTrainerPerformance = useMemo(() => {
    if (trainerFilters.cohort.length === 0) {
      return training.trainer_performance;
    }
    const cohortSet = new Set(trainerFilters.cohort);
    return (
      training.trainer_performance
        .map((row) => {
          const inScope = training.participants.filter(
            (p) =>
              p.community_unit === row.community_unit &&
              cohortSet.has(p.cohort_id),
          );
          const female = inScope.filter(
            (p) => (p.gender || "").toLowerCase() === "female",
          ).length;
          const male = inScope.filter(
            (p) => (p.gender || "").toLowerCase() === "male",
          ).length;
          const total = inScope.length;
          const target = Number(row.expected_enrollment) || 0;
          const achievement =
            target > 0 ? Math.min(Math.round((total / target) * 100), 100) : 0;
          return {
            ...row,
            female,
            male,
            total_enrollment: total,
            active: total,
            enrollment_achievement: achievement,
          };
        })
        // Hide trainers whose community has no participants in the
        // selected cohorts so the leaderboard/table stay meaningful.
        .filter((row) => row.total_enrollment > 0)
    );
  }, [
    training.trainer_performance,
    training.participants,
    trainerFilters.cohort,
  ]);

  // Summary-card totals recomputed from the filtered trainer rows.
  const filteredTrainerSummary = useMemo(() => {
    if (trainerFilters.cohort.length === 0) {
      return training.trainer_summary;
    }
    const communityUnits = new Set(
      filteredTrainerPerformance.map((r) => r.community_unit),
    ).size;
    const totalEnrollment = filteredTrainerPerformance.reduce(
      (sum, r) => sum + (Number(r.total_enrollment) || 0),
      0,
    );
    const attendanceValues = filteredTrainerPerformance
      .map((r) => Number(r.average_attendance))
      .filter((v) => Number.isFinite(v) && v > 0);
    const averageAttendance = attendanceValues.length
      ? attendanceValues.reduce((a, b) => a + b, 0) / attendanceValues.length
      : 0;
    return {
      community_units: communityUnits,
      total_enrollment: totalEnrollment,
      active: totalEnrollment,
      average_attendance: averageAttendance,
    };
  }, [
    training.trainer_summary,
    filteredTrainerPerformance,
    trainerFilters.cohort,
  ]);

  // Keep the existing binding name so the JSX below doesn't need to
  // change; the value now reflects any active cohort filter.
  const trainerSummary = filteredTrainerSummary;

  // Count of participants excluded from the trainers view for a
  // reason the cohort filter can't explain: their community has no
  // trainer row in `gsync_trainer_data`, so no trainer card can host
  // them. Only counted within the selected cohorts so the number
  // matches the delta between the participants and trainers tabs
  // (both filtered to the same cohort selection).
  const trainerExclusions = useMemo(() => {
    const trainerCommunityUnits = new Set(
      (training.trainer_performance || [])
        .map((row) => row?.community_unit)
        .filter(Boolean),
    );
    const participants = training.participants || [];
    const cohortSet = new Set(trainerFilters.cohort);
    const cohortFilterActive = cohortSet.size > 0;
    let noTrainer = 0;

    for (const participant of participants) {
      if (trainerCommunityUnits.has(participant.community_unit)) continue;
      if (cohortFilterActive && !cohortSet.has(participant.cohort_id)) continue;
      noTrainer += 1;
    }

    return { noTrainer, cohortFilterActive };
  }, [
    training.trainer_performance,
    training.participants,
    trainerFilters.cohort,
  ]);

  // Pagination applied to both the participants and trainer-performance

  const participantsPagination = usePagination({
    items: filteredParticipants,
    resetKey: filters,
    isMobile: isMobilePagination,
  });
  const trainerPagination = usePagination({
    items: filteredTrainerPerformance,
    resetKey: filteredTrainerPerformance,
    isMobile: isMobilePagination,
  });
  // Paginate the session-attendance heatmap so the card doesn't balloon
  // as weekly sessions accumulate over a cohort.
  const heatmapRows = Array.isArray(training?.session_attendance_matrix)
    ? training.session_attendance_matrix
    : [];
  const heatmapPagination = usePagination({
    items: heatmapRows,
    resetKey: heatmapRows.length,
    isMobile: isMobilePagination,
  });

  if (isLoading && (isAdmin || isManager)) {
    return (
      <div className="adminTraining-root">
        <PageHeader
          title="Training"
          description="Cohort enrollment, attendance, trainer performance, and graduation readiness."
        />
        <p className="admin-muted">Loading training metrics...</p>
      </div>
    );
  }

  return (
    <div className="adminTraining-root">
      <PageHeader
        title="Training"
        description="Cohort enrollment, attendance, trainer performance, and graduation readiness."
      />

      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      {tab === "dashboard" && (
        <>
          <div className="admin-grid admin-grid-4">
            {summaryCards.map((card, index) => {
              const Icon = SUMMARY_CARD_ICONS[card.icon] ?? BarChart3;
              return (
                <StatCard
                  key={`${card.title}-${index}`}
                  icon={Icon}
                  title={card.title}
                  value={card.value}
                  description={card.label}
                />
              );
            })}
          </div>

          <div className="admin-grid admin-grid-2">
            <Card
              title="Attendance Trend by Week"
              description="Delivered session attendance rates."
            >
              <div className="adminTraining-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={training.attendance_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Line
                      type="monotone"
                      dataKey="attendance"
                      stroke={CHART_COLORS[0]}
                      strokeWidth={3}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card
              title="Participant Status Breakdown"
              description="Active, at risk, dropped out, and graduated participants."
            >
              <div className="adminTraining-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={training.participant_status}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={95}
                      label
                    >
                      {training.participant_status.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card
              title="Cohort Performance"
              description="Enrollment and status split by cohort."
            >
              <div className="adminTraining-chart adminTraining-chart-tall">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={training.cohort_performance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="cohort" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="enrolled" fill={CHART_COLORS[0]} />
                    <Bar dataKey="active" fill={CHART_COLORS[2]} />
                    <Bar dataKey="dropped" fill={CHART_COLORS[3]} />
                    <Bar dataKey="graduated" fill={CHART_COLORS[1]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card
              title="Program Line Performance"
              description="Participants and average attendance by program line."
            >
              <div className="adminTraining-chart adminTraining-chart-tall">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={training.program_line_performance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="line" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="participants" fill={CHART_COLORS[0]} />
                    <Bar dataKey="attendance" fill={CHART_COLORS[2]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </>
      )}

      {tab === "trainers" && (
        <>
          <Card
            title="Trainer Filters"
            description="Scope trainer cards, leaderboard, table, and chart to specific cohorts. The session-attendance heatmap stays community-level because sessions aren't logged per cohort."
            className="adminTraining-filtersCard"
          >
            <div className="adminTraining-filters adminTraining-filters-single">
              <MultiSelectDropdown
                label="Cohorts"
                placeholder="All Cohorts"
                options={training.cohorts.map((cohort) => ({
                  value: cohort.id,
                  label: cohort.cohort_name,
                }))}
                selected={trainerFilters.cohort}
                onChange={(next) =>
                  setTrainerFilters({ ...trainerFilters, cohort: next })
                }
              />
            </div>
            {trainerExclusions.cohortFilterActive &&
              trainerExclusions.noTrainer > 0 && (
                <div className="adminTraining-filterHint" role="note">
                  <TrendingDown
                    className="adminTraining-filterHintIcon"
                    aria-hidden="true"
                  />
                  <span>
                    <strong>{trainerExclusions.noTrainer}</strong> Participants
                    excluded — no trainer synced for their community.
                  </span>
                </div>
              )}
          </Card>

          <div className="admin-grid admin-grid-4">
            <StatCard
              icon={Users}
              title="Community Units"
              value={trainerSummary.community_units}
              description="Synced trainer assignments"
            />
            <StatCard
              icon={UserCheck}
              title="Total Enrollment"
              value={trainerSummary.total_enrollment}
              description="Across all units"
            />
            <StatCard
              icon={CheckCircle2}
              title="Active Participants"
              value={trainerSummary.active}
              description="Retained in training"
            />
            <StatCard
              icon={TrendingUp}
              title="Average Attendance"
              value={Number(trainerSummary.average_attendance || 0).toFixed(1)}
              description="Average per community unit"
            />
          </div>

          <div className="admin-grid admin-grid-2">
            <Card
              title="Trainer Performance Leaderboard"
              description="Top trainers ranked by retention rate."
            >
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Trainer</th>
                      <th>Community Unit</th>
                      <th>Retention</th>
                      <th>Enrollment</th>
                      <th>Sessions</th>
                      <th>Avg Attendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...filteredTrainerPerformance]
                      .sort(
                        (rowA, rowB) =>
                          rowB.retention_rate - rowA.retention_rate,
                      )
                      .slice(0, 6)
                      .map((row) => {
                        const retentionLabel =
                          row.retention_rate >= 90
                            ? "Ready"
                            : row.retention_rate >= 80
                              ? "Near Ready"
                              : "At Risk";
                        return (
                          <tr key={`${row.trainer_name}-${row.community_unit}`}>
                            <td>{row.trainer_name}</td>
                            <td>{row.community_unit}</td>
                            <td>
                              <Badge tone={badgeToneForStatus(retentionLabel)}>
                                {percent(row.retention_rate)} retained
                              </Badge>
                            </td>
                            <td>{percent(row.enrollment_achievement)}</td>
                            <td>{percent(row.session_completion)}</td>
                            <td>{Number(row.average_attendance).toFixed(1)}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card
              title="Enrollment Achievement by Community Unit"
              description="Actual enrollment compared with expected enrollment."
            >
              <div className="adminTraining-chart adminTraining-chart-xl">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={filteredTrainerPerformance}
                    layout="vertical"
                    margin={{ left: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      tickFormatter={(value) => `${value}%`}
                    />
                    <YAxis
                      type="category"
                      dataKey="community_unit"
                      width={92}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip formatter={(value) => `${Math.round(value)}%`} />
                    <Bar
                      dataKey="enrollment_achievement"
                      fill={CHART_COLORS[0]}
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <Card
            title="Trainer and Community Unit Performance"
            description="Synced trainer-level operating metrics from the weekly monitoring report."
          >
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Trainer</th>
                    <th>Community Unit</th>
                    <th>Female / Male</th>
                    <th>Total</th>
                    <th>Expected</th>
                    <th>Enrollment</th>
                    <th>Active</th>
                    <th>Dropout</th>
                    <th>Sessions</th>
                    <th>Avg Attendance</th>
                    <th>Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {trainerPagination.paginated.map((row) => {
                    const isAtRisk =
                      row.retention_rate < 80 ||
                      row.average_attendance < 20 ||
                      row.enrollment_achievement < 80;
                    return (
                      <tr key={`${row.trainer_name}-${row.community_unit}`}>
                        <td>{row.trainer_name}</td>
                        <td>{row.community_unit}</td>
                        <td>
                          {row.female} / {row.male}
                        </td>
                        <td>{row.total_enrollment}</td>
                        <td>{row.expected_enrollment}</td>
                        <td>{percent(row.enrollment_achievement)}</td>
                        <td>{row.active}</td>
                        <td>
                          {row.dropouts} ({percent(row.dropout_rate)})
                        </td>
                        <td>
                          {row.sessions_delivered}/{row.planned_sessions}
                        </td>
                        <td>{Number(row.average_attendance).toFixed(1)}</td>
                        <td>
                          <Badge tone={isAtRisk ? "warning" : "success"}>
                            {isAtRisk ? "Needs follow-up" : "On track"}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <PaginationFooter
            pagination={trainerPagination}
            id="trainer-rows-per-page"
            label="trainers"
          />

          <Card
            title="Session Attendance Heatmap"
            description="Session attendance by community unit. Darker cells indicate stronger attendance."
          >
            <div className="admin-table-wrap">
              <table className="admin-table adminTraining-heatmap">
                <thead>
                  <tr>
                    <th>Session</th>
                    {heatmapUnits.map((unit) => (
                      <th key={unit}>{unit}</th>
                    ))}
                    <th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {heatmapPagination.paginated.map((row) => (
                    <tr key={row.session_name}>
                      <td>{row.session_name}</td>
                      {heatmapUnits.map((unit) => {
                        const communityAttendance = Array.isArray(
                          row.community_attendance,
                        )
                          ? row.community_attendance
                          : [];
                        const matchingEntry = communityAttendance.find(
                          (entry) => entry?.community === unit,
                        );
                        const value = Number(matchingEntry?.value) || 0;
                        return (
                          <td key={unit} className={heatmapToneClass(value)}>
                            {value}
                          </td>
                        );
                      })}
                      <td>{row.attendance_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationFooter
              pagination={heatmapPagination}
              id="heatmap-rows-per-page"
              label="sessions"
            />
          </Card>
        </>
      )}

      {tab === "participants" && (
        <>
          <Card
            title="Find Participants"
            description="Search across all participant details, and scope the list to one or more cohorts."
            className="adminTraining-filtersCard"
          >
            <div className="adminTraining-filters">
              <div className="adminTraining-search">
                <Search
                  className="adminTraining-searchIcon"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  className="adminTraining-input"
                  placeholder="Search"
                  aria-label="Search"
                  value={filters.search}
                  onChange={(event) =>
                    setFilters({ ...filters, search: event.target.value })
                  }
                />
              </div>

              {/* Multi-cohort dropdown: styled like the other filter
                  selects but hosts a checkbox list so several cohorts can
                  be picked at once. Empty selection = "All Cohorts". */}
              <MultiSelectDropdown
                label="Cohorts"
                placeholder="All Cohorts"
                options={training.cohorts.map((cohort) => ({
                  value: cohort.id,
                  label: cohort.cohort_name,
                }))}
                selected={filters.cohort}
                onChange={(next) => setFilters({ ...filters, cohort: next })}
              />
            </div>
          </Card>

          <Card
            title="Participants & Progress"
            description="Detailed participant training status, attendance, assessments, readiness, and venture transition."
          >
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Participant</th>
                    <th>ID</th>
                    <th>Cohort</th>
                    <th>Program</th>
                    <th>Community Unit</th>
                    <th>Attendance</th>
                    <th>Attended / Missed</th>
                    <th>Status</th>
                    <th>Business</th>
                    <th>Pitch</th>
                    <th>Readiness</th>
                    <th>Venture</th>
                  </tr>
                </thead>
                <tbody>
                  {participantsPagination.paginated.map((participant) => (
                    <tr key={participant.id}>
                      <td>
                        <div className="adminTraining-participantName">
                          {participant.full_name}
                        </div>
                        <div className="adminTraining-participantMeta">
                          {participant.gender}
                        </div>
                      </td>
                      <td className="adminTraining-mono">
                        {participant.participant_code}
                      </td>
                      <td>{participant.cohort_name}</td>
                      <td>{participant.program_line}</td>
                      <td>{participant.community_unit}</td>
                      <td>
                        <div className="adminTraining-attendanceCell">
                          <div className="adminTraining-progressWrap">
                            <Progress
                              value={participant.attendance_percentage}
                            />
                          </div>
                          <span className="adminTraining-attendanceValue">
                            {percent(participant.attendance_percentage)}
                          </span>
                        </div>
                        {/* Coerce to boolean — backend returns 0/1 and
                            `{0 && …}` would leak a literal "0" into the cell. */}
                        {Boolean(participant.at_risk) && (
                          <Badge tone="warning">Below 70%</Badge>
                        )}
                      </td>
                      <td>
                        {participant.attended} / {participant.missed}
                      </td>
                      <td>
                        <Badge
                          tone={badgeToneForStatus(
                            participant.enrollment_status,
                          )}
                        >
                          {participant.enrollment_status}
                        </Badge>
                      </td>
                      <td>
                        <Badge
                          tone={badgeToneForStatus(participant.business_status)}
                        >
                          {participant.business_status || "-"}
                        </Badge>
                      </td>
                      <td>
                        <Badge
                          tone={badgeToneForStatus(participant.pitch_status)}
                        >
                          {participant.pitch_status || "-"}
                        </Badge>
                      </td>
                      <td>
                        <Badge tone={badgeToneForStatus(participant.readiness)}>
                          {participant.readiness}
                        </Badge>
                      </td>
                      <td>
                        <Badge
                          tone={badgeToneForStatus(
                            participant.venture_link_status || "Not Linked",
                          )}
                        >
                          {participant.venture_link_status || "Not Linked"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredParticipants.length === 0 && (
                <div className="adminTraining-empty">
                  No synced records found for these filters.
                </div>
              )}
            </div>
          </Card>

          <PaginationFooter
            pagination={participantsPagination}
            id="participants-rows-per-page"
            label="participants"
          />
        </>
      )}
    </div>
  );
}
